/**
 * Global keyboard shortcut runtime. Mount once at the app shell; it
 * installs one window keydown listener and dispatches actions matched
 * against the combo→action_key map. Respects editable elements so
 * typing in inputs doesn't hijack keys unless the user explicitly
 * holds Cmd/Ctrl (the same heuristic the office system uses).
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildComboToAction, comboFromEvent } from './shortcutCatalog';

const QUICK_SEARCH_EVENT = 'hash:quick-search-toggle';

export default function ShortcutsRuntime() {
  const navigate = useNavigate();

  useEffect(() => {
    const comboToAction = buildComboToAction();

    const isEditableTarget = (el: EventTarget | null): boolean => {
      const node = el as HTMLElement | null;
      if (!node) return false;
      const tag = (node.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (node.isContentEditable) return true;
      return false;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      // Allow mod-combos through editable targets so the user can
      // still hit Cmd+K while typing in the search bar.
      const isModCombo = e.metaKey || e.ctrlKey;
      if (isEditableTarget(e.target) && !isModCombo) return;

      const combo = comboFromEvent(e);
      if (!combo) return;
      const action = comboToAction[combo];
      if (!action) return;

      e.preventDefault();
      e.stopPropagation();

      if (action === 'action:quick-search') {
        window.dispatchEvent(new CustomEvent(QUICK_SEARCH_EVENT));
        return;
      }
      if (action.startsWith('nav:')) {
        navigate(action.slice('nav:'.length));
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);

  return null;
}

export const QUICK_SEARCH_TOGGLE_EVENT = QUICK_SEARCH_EVENT;
